import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Badge, Row, Col, Button, Spinner, Alert, Table } from 'react-bootstrap';
import { useGetProductQuery, useGetMetadataQuery } from '../../services/api';
import PageFade from '../../components/animation/PageFade';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: productData, isLoading, error } = useGetProductQuery(id || '', { skip: !id });
  const { data: metadata } = useGetMetadataQuery();

  if (isLoading) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageFade>
    );
  }

  if (error || !productData?.data) {
    return (
      <PageFade>
        <Alert variant="danger">Product not found.</Alert>
        <Button as={Link as any} to="/products" variant="primary" className="mt-3">
          Back to Products
        </Button>
      </PageFade>
    );
  }

  // The API returns { success: true, data: Product }
  const product = (productData.data as any).data || productData.data;
  const categories = metadata?.data?.categories || [];
  const colors = metadata?.data?.colors || [];
  const sizes = metadata?.data?.sizes || [];

  // Helper to extract ID from either string or object
  const extractId = (item: any): string => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return item._id || item.id || '';
    return '';
  };

  // Helper to extract name from either string ID or populated object
  const getCategoryName = (item: any) => {
    if (typeof item === 'string') {
      return categories.find((c) => c._id === item)?.name || item;
    }
    return item?.name || extractId(item);
  };

  const getColorName = (item: any) => {
    if (typeof item === 'string') {
      return colors.find((c) => c._id === item)?.name || item;
    }
    return item?.name || extractId(item);
  };

  const getSizeName = (item: any) => {
    if (typeof item === 'string') {
      return sizes.find((s) => s._id === item)?.name || item;
    }
    return item?.name || extractId(item);
  };

  const getColorHex = (item: any) => {
    if (typeof item === 'string') {
      return colors.find((c) => c._id === item)?.hexCode || '#000000';
    }
    return item?.hexCode || '#000000';
  };

  // Handle variants if returned as JSON string
  let variants = (product as any).variants || [];
  if (typeof variants === 'string') {
    try {
      variants = JSON.parse(variants);
    } catch (e) {
      console.error('Failed to parse variants in Detail view:', e);
      variants = [];
    }
  }
  const safeVariants = Array.isArray(variants) ? variants : [];
  const totalStock = safeVariants.reduce((sum, v) => sum + (v.stock || 0), 0) || 0;

  return (
    <PageFade>
      <div className="mb-3">
        <Button variant="outline-secondary" onClick={() => navigate('/products')} className="me-2">
          ← Back to Products
        </Button>
        <Button variant="primary" onClick={() => navigate(`/products/${id}/edit`)}>
          Edit Product
        </Button>
      </div>
      <Card className="shadow-sm">
        <Card.Body>
          <Row>
            <Col md={6}>
              {product.images && product.images.length > 0 && (
                <div className="mb-3">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="img-fluid rounded"
                    style={{ maxHeight: '400px', objectFit: 'contain' }}
                  />
                  {product.images.length > 1 && (
                    <div className="mt-2 d-flex gap-2">
                      {product.images.slice(1).map((img: string, idx: number) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`${product.name} ${idx + 2}`}
                          style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Col>
            <Col md={6}>
              <Card.Title className="mb-3">{product.name}</Card.Title>
              <div className="mb-3">
                <p className="text-muted mb-1">SKU: {product.sku}</p>
                <div>
                  {product.priceBeforeDiscount ? (
                    <>
                      <span className="text-decoration-line-through text-muted me-2">
                        EGP {product.priceBeforeDiscount}
                      </span>
                      <h4 className="d-inline">EGP {product.price}</h4>
                    </>
                  ) : (
                    <h4>EGP {product.price}</h4>
                  )}
                </div>
                <div className="mt-2">
                  <Badge bg={product.inStock ? 'success' : 'danger'} className="me-2">
                    {product.inStock ? 'In Stock' : 'Out of Stock'}
                  </Badge>
                  <Badge bg={product.isVisible ? 'primary' : 'secondary'}>
                    {product.isVisible ? 'Visible' : 'Hidden'}
                  </Badge>
                </div>
              </div>
              {product.description && (
                <div className="mb-3">
                  <h6>Description</h6>
                  <p>{product.description}</p>
                </div>
              )}
              {product.material && (
                <div className="mb-2">
                  <strong>Material:</strong> {product.material}
                </div>
              )}
              {product.countryOfOrigin && (
                <div className="mb-2">
                  <strong>Country of Origin:</strong> {product.countryOfOrigin}
                </div>
              )}
              {product.careInstructions && (
                <div className="mb-3">
                  <h6>Care Instructions</h6>
                  <p className="small">{product.careInstructions}</p>
                </div>
              )}
            </Col>
          </Row>
          <hr />
          <Row>
            <Col md={12}>
              <h6>Categories</h6>
              <div className="mb-3">
                {product.categories && product.categories.length > 0 ? (
                  product.categories.map((cat: any, index: number) => {
                    const catId = extractId(cat);
                    return (
                      <Badge key={catId || `cat-${index}`} bg="secondary" className="me-2">
                        {getCategoryName(cat)}
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-muted">No categories</span>
                )}
              </div>
              <h6>Available Colors</h6>
              <div className="mb-3">
                {product.colors && product.colors.length > 0 ? (
                  product.colors.map((colorItem: any, index: number) => {
                    const colorId = extractId(colorItem);
                    const color = typeof colorItem === 'string' 
                      ? colors.find((c) => c._id === colorItem)
                      : colorItem;
                    return (
                      <Badge 
                        key={colorId || `color-${index}`} 
                        bg="secondary" 
                        className="me-2" 
                        style={{ backgroundColor: color?.hexCode || '#000000' }}
                      >
                        {getColorName(colorItem)}
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-muted">No colors</span>
                )}
              </div>
              <h6>Available Sizes</h6>
              <div className="mb-3">
                {product.sizes && product.sizes.length > 0 ? (
                  product.sizes.map((sizeItem: any, index: number) => {
                    const sizeId = extractId(sizeItem);
                    return (
                      <Badge key={sizeId || `size-${index}`} bg="info" className="me-2">
                        {getSizeName(sizeItem)}
                      </Badge>
                    );
                  })
                ) : (
                  <span className="text-muted">No sizes</span>
                )}
              </div>
                      {product.variants && safeVariants.length > 0 && (
                <div>
                  <h6>Variants & Stock</h6>
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>Color</th>
                        <th>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safeVariants.map((variant: any, idx) => {
                        const variantKey = variant._id || `${extractId(variant.size)}-${extractId(variant.color)}-${idx}`;
                        return (
                          <tr key={variantKey}>
                            <td>{getSizeName(variant.size)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: getColorHex(variant.color),
                                    borderRadius: '50%',
                                    border: '1px solid #ddd',
                                  }}
                                />
                                {getColorName(variant.color)}
                              </div>
                            </td>
                            <td>{variant.stock}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2} className="text-end">
                          <strong>Total Stock:</strong>
                        </td>
                        <td>
                          <strong>{totalStock}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </PageFade>
  );
}


